/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AbstractFormComponent,
  BuilderNavigationService,
  BuilderUserContext,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
} from '@gh/builder-shared';
import { AelEvents } from '@gh/builder-shared-data';
import { createDefaultGridModel } from '@gh/common-internal-shared';
import { ActionFlow, getAllNewSelectedEntities, getRouteInitData } from '@gh/common-shared';
import { AutocompleteItem, ID } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import {
  AutocompleteLoadMoreEvent,
  DialogService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import {
  buildEditableAutocompleteColumn,
  GridDefinition,
  GridDefinitionFactory,
} from '@gh/core-ui-grid';
import { filter$, map$ } from '@gh/rx/operators';
import { assign } from 'lodash';
import {
  AccountingAccount,
  AccountingAccountFinderDialogConfig,
  getAccountingAccountId,
  SystemVarsAccountingBusinessService,
  SystemVarsAccountingExpenseModel,
} from '../../../resources';
import { accountingAccountFinderDialogRoute } from '../accounting-account-dialog';
import { SystemVarsAccountingExpenseFormState } from './system-vars-accounting-expense-form.service';

@Component({
  standalone: false,
  selector: 'gh-system-vars-accounting-expense-subtab',
  templateUrl: 'system-vars-accounting-expense-subtab.component.html',
  styleUrls: ['system-vars-accounting-expense-subtab.component.scss'],
  viewProviders: [
    SystemVarsAccountingExpenseFormState,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SystemVarsAccountingExpenseSubtabComponent
  extends AbstractFormComponent<SystemVarsAccountingExpenseModel>
  implements OnInit {

  gridDefinition: GridDefinition;
  originalJobs: SystemVarsAccountingExpenseModel;

  constructor(
    public form: SystemVarsAccountingExpenseFormState,
    private route: ActivatedRoute,
    private translations: TranslationService,
    private gridDefinitionFactory: GridDefinitionFactory,
    private systemVarsBusiness: SystemVarsAccountingBusinessService,
    private userContext: BuilderUserContext,
    private navigationService: BuilderNavigationService,
    private pipelines: FormActionPipelines,
    private dialogService: DialogService,
    private actions: CommonActions,
  ) {
    super();
  }

  ngOnInit(): void {
    const { originalJobs } = getRouteInitData(this.route);

    assign(this, {
      originalJobs,
    });

    this.form.build(originalJobs);
    this.gridDefinition = this.buildGridDefinition();
  }

  get completeFlow(): ActionFlow<FormFlowState<SystemVarsAccountingExpenseModel>> {
    return this.pipelines.complete({
      execute: this.actions.execute((state: FormFlowState<SystemVarsAccountingExpenseModel>) =>
        this.systemVarsBusiness.updateAccountingExpense(state.form.submitValue)),
      finish: (state: FormFlowState<SystemVarsAccountingExpenseModel>) => {
        const { value } = state;
        this.form.build(value);
      },
    });
  }

  onReset(): void {
    this.dialogService
      .confirmation(yesNoConfirmation('msg_reset')).pipe(
      filter$(yesAnswer))
      .subscribe(() => {
        this.form.build(this.originalJobs);
      });
  }

  goToAel(entityId?: ID, entityType?: string, label?: string): void {
    this.navigationService.goToAel({
      entityId: entityId ? entityId : this.userContext.office!.id,
      entityType: entityType ? entityType : 'OFFICE',
      eventTypeIds: [AelEvents.Office.EditedAccountingJobExpense],
      label: label ? label : 'lbl_menu_accounting',
    });
  }

  onDelete(record: AccountingAccount): void {
    this.dialogService.confirmation(yesNoConfirmation('msg_account_delete')).pipe(
      filter$(yesAnswer))
      .subscribe(() => this.form.deleteRecord(record));
  }

  onAdd(): void {
    this.dialogService.openWithInput(accountingAccountFinderDialogRoute, <AccountingAccountFinderDialogConfig>{
        multiselection: true,
        preselected: this.form.value.jobExpenseAccounts.map(getAccountingAccountId),
      })
      .afterClosed().pipe(
      filter$(Boolean),
      map$(getAllNewSelectedEntities))
      .subscribe((accounts: AccountingAccount[]) => this.form.addRecords(accounts));
  }

  private buildGridDefinition(): GridDefinition {
    const model = createDefaultGridModel({
      editable: this.form.editable,
      getId: getAccountingAccountId,
      command: (entity: AccountingAccount, _, value) => this.form.updateCostCenter(entity, value),
    });

    const getCostCenter = (item: AutocompleteItem): string => {
      if (!item) {
        return '';
      }

      const code = item.name || '';
      const name = item.description || '';

      return `${code} ${name}`.trim();
    };

    return this.gridDefinitionFactory.buildManualWithModel({
      model,
      columns: [
        {
          field: 'code',
          headerName: this.translations.get('lbl_account'),
          width: 200,
          suppressSizeToFit: true,
        },
        {
          field: 'name',
          headerName: this.translations.get('lbl_name'),
          width: 500,
          suppressSizeToFit: true,
        },
        {
          field: 'costCenter',
          headerName: this.translations.get('lbl_cost_center'),
          width: 200,
          suppressSizeToFit: true,
          ...buildEditableAutocompleteColumn(
            {
              nameProperty: getCostCenter,
              loadMore: (event: AutocompleteLoadMoreEvent) => event.load(this.systemVarsBusiness.findCostCentersByName(
                event.query,
                [])),
            },
            model.getCellAccessor('costCenter')),
          valueFormatter: (params: any) => getCostCenter(params.data.costCenter),
        },
      ],
    });
  }
}
